import React, { useState } from 'react';
import { fetchProductData, saveProductData, getProductDataTimestamp, needsProductDataRefresh } from '../../utils/productData';
import './ProductDataUpdater.css';

interface ProductDataUpdaterProps {
  onUpdateComplete?: () => void;
}

const ProductDataUpdater: React.FC<ProductDataUpdaterProps> = ({ onUpdateComplete }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<{
    message: string;
    isError: boolean;
  } | null>(null);

  // Format date for display
  const formatDate = (timestamp: number | null): string => {
    if (!timestamp) return 'Never';
    
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Handle update button click
  const handleUpdateClick = async () => {
    setIsLoading(true);
    setStatus(null);
    
    try {
      const data = await fetchProductData();
      saveProductData(data);
      
      setStatus({
        message: `Successfully updated ${data.length} products`,
        isError: false
      });
      
      if (onUpdateComplete) {
        onUpdateComplete();
      }
    } catch (error) {
      setStatus({
        message: `Error updating product data: ${error instanceof Error ? error.message : 'Unknown error'}`,
        isError: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  const lastUpdated = getProductDataTimestamp();
  const needsRefresh = needsProductDataRefresh();

  return (
    <div className="product-data-updater">
      <div className="product-data-info">
        <span className="last-updated">
          Last updated: {formatDate(lastUpdated)}
        </span>
        {needsRefresh && (
          <span className="refresh-needed">
            Update recommended
          </span>
        )}
      </div>
      
      <button 
        className={`update-button ${needsRefresh ? 'update-needed' : ''}`}
        onClick={handleUpdateClick}
        disabled={isLoading}
      >
        {isLoading ? 'Updating...' : 'Update Product Data'}
      </button>
      
      {status && (
        <div className={`status-message ${status.isError ? 'error' : 'success'}`}>
          {status.message}
        </div>
      )}
    </div>
  );
};

export default ProductDataUpdater;
